import {
  TASKS_PATH,
  CHATS_PATH,
  DATE_RANGE_TYPE,
  HOME_PATH,
  THREADS_PATH,
  UserPreferenceName,
} from "modules/utils/Constants";
import {
  MessageThreadSortType,
  SearchFilter,
  SearchFilterType,
} from "gql/graphql";
import useUserPreference, { DATE_FILTER_VALUE } from "./use-user-preference";
import useSearchFilter from "./use-search-filter";
import { useMemo } from "react";
import { useAuth } from "modules/providers/auth-provider";

interface IProps {
  path: string;
}

const useStickyFilter = ({ path }: IProps) => {
  const { getUrl, getStartDate } = useSearchFilter();
  const { me } = useAuth();

  //DATE FILTERS
  const _dateFilterName = useMemo((): UserPreferenceName | undefined => {
    switch (path) {
      case HOME_PATH: {
        return "WEB_HOME_DATE_FILTER";
      }
      case THREADS_PATH: {
        return "WEB_THREADS_DATE_FILTER";
      }
      case CHATS_PATH: {
        return "WEB_CHATS_DATE_FILTER";
      }
      default:
        return undefined;
    }
  }, [path]);

  const { stringValue: dateFilterValue } = useUserPreference({
    userId: me?.id,
    name: _dateFilterName,
  });

  const dateSearchFilters = useMemo((): SearchFilter[] => {
    if (dateFilterValue === DATE_FILTER_VALUE.TODAY) {
      return [
        {
          type: SearchFilterType.DateRangeType,
          stringValue: DATE_RANGE_TYPE.LAST_24_HOURS,
        },
        {
          type: SearchFilterType.StartDate,
          instantValue: getStartDate(DATE_RANGE_TYPE.LAST_24_HOURS),
        },
      ];
    }
    if (dateFilterValue === DATE_FILTER_VALUE.THIS_WEEK) {
      return [
        {
          type: SearchFilterType.DateRangeType,
          stringValue: DATE_RANGE_TYPE.LAST_7_DAYS,
        },
        {
          type: SearchFilterType.StartDate,
          instantValue: getStartDate(DATE_RANGE_TYPE.LAST_7_DAYS),
        },
      ];
    }
    return [];
  }, [dateFilterValue, getStartDate]);

  //UNREAD FILTER
  const _isUnreadFilterToggleName = useMemo(():
    | UserPreferenceName
    | undefined => {
    switch (path) {
      case HOME_PATH: {
        return "WEB_IS_HOME_UNREAD_FILTER_TOGGLED";
      }
      case THREADS_PATH: {
        return "WEB_IS_THREADS_UNREAD_FILTER_TOGGLED";
      }
      case CHATS_PATH: {
        return "WEB_IS_CHATS_UNREAD_FILTER_TOGGLED";
      }
      case TASKS_PATH: {
        return "WEB_IS_TASKS_UNREAD_FILTER_TOGGLED";
      }
      default:
        return undefined;
    }
  }, [path]);

  const { booleanValue: isUnreadFilterToggled } = useUserPreference({
    userId: me?.id,
    name: _isUnreadFilterToggleName,
  });

  const unreadFilters = useMemo((): SearchFilter[] => {
    if (isUnreadFilterToggled === true) {
      return [
        {
          type: SearchFilterType.IsNotSeenByMe,
        },
      ];
    }
    return [];
  }, [isUnreadFilterToggled]);

  const { stringArrayValue: taskAssigneeUserIds } = useUserPreference({
    userId: me?.id,
    name: "WEB_TASK_ASSIGNED_USER_FILTER",
  });

  const taskAssigneeFilters = useMemo((): SearchFilter[] => {
    if (!me) return [];
    switch (path) {
      case TASKS_PATH: {
        if (taskAssigneeUserIds && taskAssigneeUserIds.length > 0) {
          return [
            {
              type: SearchFilterType.TaskAssigneeUserIdList,
              objectIdListValue: taskAssigneeUserIds,
            },
          ];
        }
        return [];
      }
      default:
        return [];
    }
  }, [taskAssigneeUserIds, me, path]);

  const { stringArrayValue: taskCreatedUserIds } = useUserPreference({
    userId: me?.id,
    name: "WEB_TASK_CREATED_USER_FILTER",
  });

  const taskCreatedFilters = useMemo((): SearchFilter[] => {
    if (!me) return [];
    switch (path) {
      case TASKS_PATH: {
        if (taskCreatedUserIds && taskCreatedUserIds.length > 0) {
          return [
            {
              type: SearchFilterType.CreatedUserIdList,
              objectIdListValue: taskCreatedUserIds,
            },
          ];
        }
        return [];
      }
      default:
        return [];
    }
  }, [me, path, taskCreatedUserIds]);

  const { stringArrayValue: taskStatuses } = useUserPreference({
    userId: me?.id,
    name: "WEB_TASK_STATUS_FILTER",
  });

  const taskStatusFilters = useMemo((): SearchFilter[] => {
    if (!me) return [];
    switch (path) {
      case TASKS_PATH: {
        if (taskStatuses && taskStatuses.length > 0) {
          return [
            {
              type: SearchFilterType.TaskStatusTypeList,
              stringListValue: taskStatuses,
            },
          ];
        }
        return [];
      }
      default:
        return [];
    }
  }, [me, path, taskStatuses]);

  const { stringValue: sortType } = useUserPreference({
    userId: me?.id,
    name: "WEB_SORT_TYPE",
  });

  const sortTypeFilter = useMemo((): MessageThreadSortType | undefined => {
    if (!me) return undefined;
    switch (path) {
      case TASKS_PATH: {
        return sortType as MessageThreadSortType | undefined;
      }
      default:
        return undefined;
    }
  }, [me, path, sortType]);

  const url = useMemo(
    () =>
      getUrl(
        path,
        sortTypeFilter,
        ...[
          ...dateSearchFilters,
          ...unreadFilters,
          ...taskAssigneeFilters,
          ...taskCreatedFilters,
          ...taskStatusFilters,
        ],
      ),
    [
      getUrl,
      path,
      sortTypeFilter,
      dateSearchFilters,
      unreadFilters,
      taskAssigneeFilters,
      taskCreatedFilters,
      taskStatusFilters,
    ],
  );

  return {
    url,
  };
};

export default useStickyFilter;
